import React, { useEffect, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { Loader2, CheckCircle, XCircle } from "lucide-react";
import api from "@/lib/api";

export default function OuraCallbackPage() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [status, setStatus] = useState("processing");
  const [error, setError] = useState(null);

  useEffect(() => {
    const handleCallback = async () => {
      const code = searchParams.get("code");
      const errorParam = searchParams.get("error");

      if (errorParam) {
        setStatus("error");
        setError("Authorization was denied");
        return;
      }

      if (!code) {
        setStatus("error");
        setError("No authorization code received");
        return;
      }

      try {
        await api.post("/oura/exchange-token", {
          code,
          redirect_uri: `${window.location.origin}/connections/oura/callback`
        });
        
        setStatus("success");
        
        // Sync data immediately after connection
        try {
          await api.post("/oura/sync");
        } catch (syncErr) {
          console.error("Initial sync error:", syncErr);
        }
        
        // Redirect after short delay
        setTimeout(() => {
          navigate("/dashboard", { replace: true });
        }, 2000);
      } catch (err) {
        console.error("Oura callback error:", err);
        setStatus("error");
        setError(err.response?.data?.detail || "Failed to connect Oura Ring");
      }
    };

    handleCallback();
  }, [searchParams, navigate]);

  return (
    <div className="min-h-screen bg-black flex items-center justify-center p-6">
      <div className="text-center">
        {status === "processing" && (
          <>
            <Loader2 className="w-12 h-12 text-[#00FF88] animate-spin mx-auto mb-4" />
            <h2 className="text-xl font-semibold text-white mb-2" style={{ fontFamily: 'Manrope' }}>
              Connecting Oura Ring
            </h2>
            <p className="text-slate-500">Please wait while we set up your connection...</p>
          </>
        )}

        {status === "success" && (
          <>
            <CheckCircle className="w-12 h-12 text-emerald-400 mx-auto mb-4" />
            <h2 className="text-xl font-semibold text-white mb-2" style={{ fontFamily: 'Manrope' }}>
              Oura Ring Connected!
            </h2>
            <p className="text-slate-500">Your health data is now syncing...</p>
            <p className="text-xs text-slate-600 mt-4">Redirecting to dashboard...</p>
          </>
        )}

        {status === "error" && (
          <>
            <XCircle className="w-12 h-12 text-red-400 mx-auto mb-4" />
            <h2 className="text-xl font-semibold text-white mb-2" style={{ fontFamily: 'Manrope' }}>
              Connection Failed
            </h2>
            <p className="text-slate-500 mb-4">{error}</p>
            <button
              onClick={() => navigate("/connections")}
              className="px-6 py-2 bg-white/10 rounded-full text-white text-sm hover:bg-white/20 transition-colors"
            >
              Try Again
            </button>
          </>
        )}
      </div>
    </div>
  );
}
